import { useCallback, useState } from "react"

export interface IInlineTermEdit {
  /** Id of the suggestion currently being edited, or null. */
  editingId: string | null
  /** Draft text in the editor. */
  draft: string
  /** Update the draft text. */
  setDraft: (value: string) => void
  /** Enter edit mode for a row, seeding the draft with its current term. */
  startEdit: (id: string, term: string) => void
  /** Leave edit mode without accepting. */
  cancelEdit: () => void
  /** True when the given row is the one being edited. */
  isEditing: (id: string) => boolean
  /**
   * The value to hand to `onAccept` for a row: the trimmed draft when it's being
   * edited to something different, otherwise `undefined` (accept the term as-is).
   */
  acceptValue: (id: string, term: string) => string | undefined
}

/**
 * Local UI state for click-to-edit term rows (jargon suggestions). Keeps the
 * host components dumb — it owns no data, only the "which row is open + draft"
 * bookkeeping shared by the meeting card and the Jargon Manager list.
 */
export function useInlineTermEdit(): IInlineTermEdit {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState("")

  const startEdit = useCallback((id: string, term: string): void => {
    setEditingId(id)
    setDraft(term)
  }, [])

  const cancelEdit = useCallback((): void => {
    setEditingId(null)
    setDraft("")
  }, [])

  const isEditing = useCallback((id: string): boolean => editingId === id, [editingId])

  const acceptValue = useCallback(
    (id: string, term: string): string | undefined => {
      if (editingId !== id) return undefined
      const trimmed = draft.trim()
      return trimmed.length > 0 && trimmed !== term ? trimmed : undefined
    },
    [editingId, draft],
  )

  return { editingId, draft, setDraft, startEdit, cancelEdit, isEditing, acceptValue }
}
